import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import {
  SUPPORTED_LANGUAGES,
  LANGUAGE_STORAGE_KEY,
} from "../i18n";

/**
 * LanguageSelector
 *
 * Text-based dropdown (no flag-only labels — flags don't map 1:1 to
 * languages).  Emits the selected language to:
 *   - i18next (immediate UI update),
 *   - localStorage (guest persistence + first-paint on next visit),
 *   - backend `/api/users/me/language` (only if the user is authenticated).
 *
 * `variant="ghost"`  — used inline in Navigation/Footer.
 * `variant="panel"` — used inside settings screens (larger radio-like list).
 */
export default function LanguageSelector({ variant = "ghost" }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);

  const current = (i18n.language || "en").split("-")[0];
  const options = [
    { code: "en", label: t("language.english") },
    { code: "it", label: t("language.italian") },
  ].filter((o) => SUPPORTED_LANGUAGES.includes(o.code));

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const change = async (code) => {
    if (code === current) {
      setOpen(false);
      return;
    }
    try {
      setSaving(true);
      await i18n.changeLanguage(code);
      // i18next-browser-languagedetector already writes localStorage but we
      // force-set it here so the write happens even when detection cache is
      // disabled by a future refactor.
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
      } catch (_e) {
        // Storage may be blocked (private mode / SSR) — non-fatal.
      }
      if (user) {
        try {
          await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/users/me/language`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ language: code }),
            },
          );
        } catch (_e) {
          // Non-fatal: local preference already applied.
        }
      }
    } finally {
      setSaving(false);
      setOpen(false);
    }
  };

  if (variant === "panel") {
    return (
      <div
        className="space-y-2"
        data-testid="language-selector-panel"
      >
        <p className="font-heading text-xs tracking-widest text-gold uppercase">
          {t("language.label")}
        </p>
        <div className="grid gap-2">
          {options.map((o) => (
            <button
              key={o.code}
              type="button"
              onClick={() => change(o.code)}
              disabled={saving}
              className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border transition-all font-heading text-sm ${
                current === o.code
                  ? "border-gold bg-gold/10 text-gold"
                  : "border-border bg-card hover:border-gold/60 hover:bg-gold/5 text-foreground"
              }`}
              data-testid={`language-option-${o.code}`}
            >
              <span>{o.label}</span>
              {current === o.code && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const currentLabel =
    options.find((o) => o.code === current)?.label || t("language.english");

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("language.switch")}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border hover:border-gold/60 text-foreground hover:text-gold transition-colors font-heading text-xs uppercase tracking-wider"
        data-testid="language-selector-button"
      >
        <Globe className="w-4 h-4" />
        <span>{currentLabel}</span>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 w-40 bg-card border border-border rounded-lg shadow-xl overflow-hidden z-50"
          data-testid="language-selector-menu"
        >
          {options.map((o) => (
            <li key={o.code} role="option" aria-selected={current === o.code}>
              <button
                type="button"
                onClick={() => change(o.code)}
                disabled={saving}
                className={`flex items-center justify-between w-full px-4 py-2 text-sm transition-colors ${
                  current === o.code
                    ? "text-gold bg-gold/10"
                    : "text-foreground hover:bg-gold/10 hover:text-gold"
                }`}
                data-testid={`language-option-${o.code}`}
              >
                <span>{o.label}</span>
                {current === o.code && <Check className="w-4 h-4" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
